import {Moment} from "moment";
import {Buttons} from "../../components/buttons/Buttons";
import {LoaderTab} from "../../components/Utils/loader/LoaderTab";
import {AppTranslations} from "../../components/Utils/translations/AppTranslations";
import {MarketingConfig} from "./data/MarketingConfig";
import {MarketingData} from "./data/MarketingData";
import {PeriodChanger, PeriodSelection} from "./elements/PeriodChanger";
import {TimeChanger} from "./elements/TimeChanger";
import {SelectOption, UnitOfMeasureChanger} from "./elements/UnitOfMeasureChanger";
import "./MarketingTab.scss";
import {MarketingTabMonth} from "./section/MarketingTabMonth";
import {MarketingTabRolling4Weeks} from "./section/MarketingTabRolling4Weeks";
import {MarketingTabRollingQuarter} from "./section/MarketingTabRollingQuarter";
import {MarketingTabSemesterToDate} from "./section/MarketingTabRollingSemester";
import {MarketingTabRollingYear} from "./section/MarketingTabRollingYear";
import {MarketingTabWeek} from "./section/MarketingTabWeek";
import {MarketingTabYear} from "./section/MarketingTabYear";
import AutoSizer from "react-virtualized-auto-sizer";
import moment = require("moment");
import React = require("react");
import UnitOfMeasure = MarketingConfig.UnitOfMeasure;
import MarketingDataConverter = MarketingData.MarketingDataConverter;

declare let LOCALE_DATE_MOMENT: string;
declare let AppTranslations: AppTranslations;

enum MarketingTabStatus {
    /** The tab is fetching the configuration from the server. */
    Initializing,
    /** The tab is initialized but has no data yet.. */
    Initialized,
    /** The tab is fetching the data from the server. */
    Loading,
    /** The tab is ready to display the data. */
    Ready,
    /** An error has occurred. */
    Error,
}

interface MarketingTabProps {
    /** String because they must be immutable. */
    filters: string;
    /** Function to open the location tab. */
    goToLocation: () => any;
    isDetailLevel: boolean;
    componentCategory: MarketingConfig.MarketingCategory;
}

interface MarketingTabState {
    /** True until the configuration has been fetched. */
    initializing: boolean;
    /** Status of the week report */
    weekStatus: MarketingTabStatus;
    /** Status of the month report */
    monthStatus: MarketingTabStatus;
    /** Status of the year report */
    yearStatus: MarketingTabStatus;
    /** Status of the rolling 4 weeks report */
    rolling4WeeksStatus: MarketingTabStatus;
    /** Status of the rolling quarter report */
    rollingQuarterStatus: MarketingTabStatus;
    /** Status of the rolling semester report */
    rollingSemesterStatus: MarketingTabStatus;
    /** Status of the rolling year report */
    rollingYearStatus: MarketingTabStatus;
    /** Titles, unit measures, ecc... to initialized the tab and the reports */
    configuration: MarketingConfig.Container;
    /**  Data for the week report */
    dataWeek: MarketingData.PeriodData;
    /** Data for the month report */
    dataMonth: MarketingData.PeriodData;
    /**  Data for the year report */
    dataYear: MarketingData.PeriodData;
    /**  Data for the rolling 4 weeks report */
    dataRolling4Weeks: MarketingData.RollingPeriodWithWeeklySales;
    /**  Data for the rolling quarter report */
    dataRollingQuarter: MarketingData.RollingPeriodWithMonthlySales;
    /**  Data for the rolling semester report */
    dataRollingSemester: MarketingData.RollingPeriodWithMonthlySales;
    /** Data for the rolling year report */
    dataRollingYear: MarketingData.RollingPeriodWithMonthlySales;
    /** Selected date of the week report */
    dateWeek: string;
    /** Selected date of the month report */
    dateMonth: string;
    /** Selected date of the year report */
    dateYear: string;
    /** Selected date of the rolling 4 weeks report */
    dateRolling4Weeks: string;
    /** Selected date of the rolling quarter report */
    dateRollingQuarter: string;
    /** Selected date of the rolling semester report */
    dateRollingSemester: string;
    /** Selected date of the rolling year report */
    dateRollingYear: string;
    /** Currently selected period */
    period: PeriodSelection;
    /** Currently selected unit of measure */
    unitOfMeasure: UnitOfMeasure;
    unitOfMeasureInQuantityMode: UnitOfMeasure;
}

export class MarketingTab extends React.Component<MarketingTabProps, MarketingTabState> {

    /**
     *  The value in pixels when the marketing container must be rendered in reduced or extended mode
     */
    public static CONTAINER_BREAKPOINT = 1110;
    private static logEnabled: boolean = false;

    /**
     * Format the given date to be displayed to the user.
     */
    private static formatUserDate(date: Moment): string {
        return date.format(LOCALE_DATE_MOMENT);
    }

    constructor(props: MarketingTabProps) {
        super(props);
        this.log("Initializing");
        this.changePeriod = this.changePeriod.bind(this);
        this.changeTime = this.changeTime.bind(this);
        this.onUnitOfMeasureSelection = this.onUnitOfMeasureSelection.bind(this);
        this.exportToExcel = this.exportToExcel.bind(this);
        this.state = {
            initializing: true,
            monthStatus: MarketingTabStatus.Initializing,
            rollingYearStatus: MarketingTabStatus.Initializing,
            yearStatus: MarketingTabStatus.Initializing,
            rolling4WeeksStatus: MarketingTabStatus.Initializing,
            rollingQuarterStatus: MarketingTabStatus.Initializing,
            rollingSemesterStatus: MarketingTabStatus.Initializing,
            weekStatus: MarketingTabStatus.Initializing,
            configuration: null,
            dataMonth: null,
            dataRollingYear: null,
            dataRollingQuarter: null,
            dataRolling4Weeks: null,
            dataRollingSemester: null,
            dataYear: null,
            dataWeek: null,
            period: PeriodSelection.Week,
            dateWeek: TimeChanger.currentDate(PeriodSelection.Week),
            dateMonth: TimeChanger.currentDate(PeriodSelection.Month),
            dateYear: TimeChanger.currentDate(PeriodSelection.Year),
            dateRolling4Weeks: TimeChanger.currentDate(PeriodSelection.Rolling4Weeks),
            dateRollingQuarter: TimeChanger.currentDate(PeriodSelection.RollingQuarter),
            dateRollingSemester: TimeChanger.currentDate(PeriodSelection.RollingSemester),
            dateRollingYear: TimeChanger.currentDate(PeriodSelection.RollingYear),
            unitOfMeasure: this.props.componentCategory.salesUnitOfMeasure,
            unitOfMeasureInQuantityMode: null,
        };
        this.retrieveConfiguration();
    }

    public componentWillUpdate(nextProps: Readonly<MarketingTabProps>,
                               nextState: Readonly<MarketingTabState>,
                               nextContext: any) {
        this.log("Component will update");
        if (nextProps.filters !== this.props.filters
            || nextProps.componentCategory.categoryGuid !== this.props.componentCategory.categoryGuid) {
            this.log("The filters have changed");
            this.setState({
                weekStatus: MarketingTabStatus.Initialized,
                monthStatus: MarketingTabStatus.Initialized,
                yearStatus: MarketingTabStatus.Initialized,
                rolling4WeeksStatus: MarketingTabStatus.Initialized,
                rollingQuarterStatus: MarketingTabStatus.Initialized,
                rollingSemesterStatus: MarketingTabStatus.Initialized,
                rollingYearStatus: MarketingTabStatus.Initialized,
                dataWeek: null,
                dataMonth: null,
                dataYear: null,
                dataRolling4Weeks: null,
                dataRollingQuarter: null,
                dataRollingSemester: null,
                dataRollingYear: null,
                unitOfMeasure: this.props.componentCategory.categoryGuid !== nextProps.componentCategory.categoryGuid
                && MarketingDataConverter.isUnitsUnitOfMeasure(nextProps.componentCategory.salesUnitOfMeasure) ?
                    nextProps.componentCategory.salesUnitOfMeasure :
                    this.state.unitOfMeasureInQuantityMode,
            });
        }
        if (nextState.period === PeriodSelection.Week) {
            if (nextState.weekStatus === MarketingTabStatus.Initialized) {
                this.log("Week data must be retrieved");
                this.setState({weekStatus: MarketingTabStatus.Loading});
                this.retrieveWeekData(nextState.dateWeek);
                return;
            }
        } else if (nextState.period === PeriodSelection.Month) {
            if (nextState.monthStatus === MarketingTabStatus.Initialized) {
                this.log("Month to date data must be retrieved");
                this.setState({monthStatus: MarketingTabStatus.Loading});
                this.retrieveMonthData(nextState.dateMonth);
                return;
            }
        } else if (nextState.period === PeriodSelection.Year) {
            if (nextState.yearStatus === MarketingTabStatus.Initialized) {
                this.log("Year to date data must be retrieved");
                this.setState({yearStatus: MarketingTabStatus.Loading});
                this.retrieveYearData(nextState.dateYear);
                return;
            }
        } else if (nextState.period === PeriodSelection.Rolling4Weeks) {
            if (nextState.rolling4WeeksStatus === MarketingTabStatus.Initialized) {
                this.log("Rolling 4 weeks data must be retrieved");
                this.setState({rolling4WeeksStatus: MarketingTabStatus.Loading});
                this.retrieveRolling4WeeksData(nextState.dateRolling4Weeks);
                return;
            }
        } else if (nextState.period === PeriodSelection.RollingQuarter) {
            if (nextState.rollingQuarterStatus === MarketingTabStatus.Initialized) {
                this.log("Quarter to date data must be retrieved");
                this.setState({rollingQuarterStatus: MarketingTabStatus.Loading});
                this.retrieveRollingQuarterData(nextState.dateRollingQuarter);
                return;
            }
        } else if (nextState.period === PeriodSelection.RollingSemester) {
            if (nextState.rollingSemesterStatus === MarketingTabStatus.Initialized) {
                this.log("Semester to date data must be retrieved");
                this.setState({rollingSemesterStatus: MarketingTabStatus.Loading});
                this.retrieveRollingSemesterData(nextState.dateRollingSemester);
                return;
            }
        } else if (nextState.period === PeriodSelection.RollingYear) {
            if (nextState.rollingYearStatus === MarketingTabStatus.Initialized) {
                this.log("Rolling year data must be retrieved");
                this.setState({rollingYearStatus: MarketingTabStatus.Loading});
                this.retrieveRollingYearData(nextState.dateRollingYear);
                return;
            }
        }
    }

    public render() {
        this.log("Rendering");
        this.log(this.state);
        if (this.state.initializing) {
            return <LoaderTab/>;
        }
        const title = this.buildTitle(this.state.configuration, this.state.period);
        let content;
        if (this.state.period === PeriodSelection.Month) {
            if (this.state.monthStatus !== MarketingTabStatus.Ready) {
                content = <LoaderTab/>;
            } else {
                content = (
                    <MarketingTabMonth
                        configuration={this.state.configuration}
                        dataMonth={this.state.dataMonth}
                        goToLocation={this.props.goToLocation}
                        unitOfMeasure={this.state.unitOfMeasure}
                        isDetailLevel={this.props.isDetailLevel}
                    />
                );
            }
        } else if (this.state.period === PeriodSelection.RollingYear) {
            if (this.state.rollingYearStatus !== MarketingTabStatus.Ready) {
                content = <LoaderTab/>;
            } else {
                content = (
                    <MarketingTabRollingYear
                        configuration={this.state.configuration}
                        dataRollingYear={this.state.dataRollingYear}
                        goToLocation={this.props.goToLocation}
                        unitOfMeasure={this.state.unitOfMeasure}
                        isDetailLevel={this.props.isDetailLevel}
                    />
                );
            }
        } else if (this.state.period === PeriodSelection.Year) {
            if (this.state.yearStatus !== MarketingTabStatus.Ready) {
                content = <LoaderTab/>;
            } else {
                content = (
                    <MarketingTabYear
                        configuration={this.state.configuration}
                        dataYear={this.state.dataYear}
                        goToLocation={this.props.goToLocation}
                        unitOfMeasure={this.state.unitOfMeasure}
                        isDetailLevel={this.props.isDetailLevel}
                    />
                );
            }
        } else if (this.state.period === PeriodSelection.Rolling4Weeks) {
            if (this.state.rolling4WeeksStatus !== MarketingTabStatus.Ready) {
                content = <LoaderTab/>;
            } else {
                content = (
                    <MarketingTabRolling4Weeks
                        configuration={this.state.configuration}
                        dataRolling4Weeks={this.state.dataRolling4Weeks}
                        goToLocation={this.props.goToLocation}
                        unitOfMeasure={this.state.unitOfMeasure}
                        isDetailLevel={this.props.isDetailLevel}
                    />
                );
            }
        } else if (this.state.period === PeriodSelection.RollingQuarter) {
            if (this.state.rollingQuarterStatus !== MarketingTabStatus.Ready) {
                content = <LoaderTab/>;
            } else {
                content = (
                    <MarketingTabRollingQuarter
                        configuration={this.state.configuration}
                        dataRollingQuarter={this.state.dataRollingQuarter}
                        goToLocation={this.props.goToLocation}
                        unitOfMeasure={this.state.unitOfMeasure}
                        isDetailLevel={this.props.isDetailLevel}
                    />
                );
            }
        } else if (this.state.period === PeriodSelection.RollingSemester) {
            if (this.state.rollingSemesterStatus !== MarketingTabStatus.Ready) {
                content = <LoaderTab/>;
            } else {
                content = (
                    <MarketingTabSemesterToDate
                        configuration={this.state.configuration}
                        dataRollingSemester={this.state.dataRollingSemester}
                        goToLocation={this.props.goToLocation}
                        unitOfMeasure={this.state.unitOfMeasure}
                        isDetailLevel={this.props.isDetailLevel}
                    />
                );
            }
        } else if (this.state.period === PeriodSelection.Week) {
            if (this.state.weekStatus !== MarketingTabStatus.Ready) {
                content = <LoaderTab/>;
            } else {
                content = (
                    <MarketingTabWeek
                        configuration={this.state.configuration}
                        dataWeek={this.state.dataWeek}
                        goToLocation={this.props.goToLocation}
                        unitOfMeasure={this.state.unitOfMeasure}
                        isDetailLevel={this.props.isDetailLevel}
                    />
                );
            }
        }

        let selectedDate;
        switch (this.state.period) {
            case PeriodSelection.Week:
                selectedDate = this.state.dateWeek;
                break;
            case PeriodSelection.Month:
                selectedDate = this.state.dateMonth;
                break;
            case PeriodSelection.Year:
                selectedDate = this.state.dateYear;
                break;
            case PeriodSelection.Rolling4Weeks:
                selectedDate = this.state.dateRolling4Weeks;
                break;
            case PeriodSelection.RollingQuarter:
                selectedDate = this.state.dateRollingQuarter;
                break;
            case PeriodSelection.RollingSemester:
                selectedDate = this.state.dateRollingSemester;
                break;
            case PeriodSelection.RollingYear:
                selectedDate = this.state.dateRollingYear;
                break;
            default:
                console.error("PeriodSelection " + this.state.period + " not recognized");
        }
        const onExportToExcel = () => {
            this.exportToExcel(selectedDate);
        };
        const measuresOptions: SelectOption[] = [];
        let unitOfMeasureChanger = null;
        if (!MarketingDataConverter.isUnitsUnitOfMeasure(this.props.componentCategory.salesUnitOfMeasure)) {
            this.state.configuration.unitOfMeasures.forEach((u) => {
                measuresOptions.push({value: u.code, label: u.name});
            });
            unitOfMeasureChanger = (
                <React.Fragment>
                    <span className="marketing-tab__tools__labels">
                        {this.state.configuration.unitOfMeasureChangeTitle}
                    </span>
                    <UnitOfMeasureChanger
                        onUnitOfMeasureSelection={this.onUnitOfMeasureSelection}
                        unitOfMeasure={this.state.unitOfMeasure}
                        options={measuresOptions}
                    />
                </React.Fragment>
            );
        }
        return (
            <AutoSizer disableHeight style={{width: "auto"}}>
                {({width}) => <div
                    className={"marketing-tab"
                        + (width <= MarketingTab.CONTAINER_BREAKPOINT
                                ? "  marketing-tab--minimized"
                                : ""
                        )}
                >
                    <div className="marketing-tab__header">
                        <PeriodChanger
                            period={this.state.period}
                            onPeriodSelection={this.changePeriod}
                            textWeek={this.state.configuration.periods.week}
                            textMonth={this.state.configuration.periods.month}
                            textYear={this.state.configuration.periods.year}
                            textRolling4Weeks={this.state.configuration.periods.rolling4Weeks}
                            textRollingQuarter={this.state.configuration.periods.rollingQuarter}
                            textRollingSemester={this.state.configuration.periods.rollingSemester}
                            textRollingYear={this.state.configuration.periods.rollingYear}
                        />
                        <div className="marketing-tab__tools">
                            <span className="marketing-tab__tools--labels">
                                {this.state.configuration.timeChangeTitle}
                            </span>
                            <TimeChanger
                                period={this.state.period}
                                onTimeSelection={this.changeTime}
                                startingDate={this.state.configuration.startingDate}
                                selectedDate={selectedDate}
                            />
                            {unitOfMeasureChanger}
                        </div>
                        <div style={{float: "right"}}>
                            <Buttons.ExportToExcel
                                onClick={onExportToExcel}
                                label={AppTranslations.miscellaneous.exportToExcel}
                            />
                        </div>
                        <p className="marketing-tab__title">{title}</p>
                    </div>
                    {content}
                </div>}
            </AutoSizer>
        );
    }

    private onUnitOfMeasureSelection(unitOfMeasure: string) {
        const um = this.state.configuration.unitOfMeasures.filter(
            (u) => {
                return u.code === unitOfMeasure;
            })[0];
        this.setState(
            {unitOfMeasureInQuantityMode: um, unitOfMeasure: um},
        );
    }

    private retrieveConfiguration(): void {
        this.log("Retrieving configuration");
        const url = this.props.isDetailLevel ? "location/marketing/configuration" : "locations/marketing/configuration";
        Ajax.ajaxRequestForJson(
            {
                url,
                type: "GET",
                success: (response: MarketingConfig.Container) => {
                    this.log("Success retrieving configuration");
                    this.log(response);
                    const unitOfMeasureInQuantityMode = response.unitOfMeasures
                        .filter((um) => um.code === response.defaultUnitOfMeasureCode)[0];
                    this.setState({
                            initializing: false,
                            weekStatus: MarketingTabStatus.Initialized,
                            monthStatus: MarketingTabStatus.Initialized,
                            yearStatus: MarketingTabStatus.Initialized,
                            rolling4WeeksStatus: MarketingTabStatus.Initialized,
                            rollingQuarterStatus: MarketingTabStatus.Initialized,
                            rollingSemesterStatus: MarketingTabStatus.Initialized,
                            rollingYearStatus: MarketingTabStatus.Initialized,
                            configuration: response,
                            unitOfMeasureInQuantityMode,
                            unitOfMeasure: this.props.componentCategory.salesUnitOfMeasure,
                        },
                    );
                },
            },
        );
    }

    private retrieveWeekData(date: string) {
        this.log("Retrieving week data: " + date);
        // Copy the filters to check them when the data is retrieved.
        const filters = this.props.filters;
        const categoryGuid = this.props.componentCategory.categoryGuid;
        Ajax.ajaxRequestForJson(
            {
                url: "locations/marketing/" + this.props.componentCategory.categoryGuid + "/week/" + date,
                data: filters,
                type: "POST",
                success: (data: MarketingData.PeriodData) => {
                    if (this.props.filters === filters
                        && this.props.componentCategory.categoryGuid === categoryGuid
                        && this.state.dateWeek === date) {
                        this.log("Success retrieving week data");
                        this.log(data);
                        this.setState({
                            weekStatus: MarketingTabStatus.Ready,
                            dataWeek: data,
                        });
                    } else {
                        this.log("Retrieved week data but filters/date changed");
                    }
                },
            },
        );
    }

    private retrieveMonthData(date: string): void {
        this.log("Retrieving month to date data: " + date);
        // Copy the filters to check them when the data is retrieved.
        const filters = this.props.filters;
        const categoryGuid = this.props.componentCategory.categoryGuid;
        Ajax.ajaxRequestForJson(
            {
                url: "locations/marketing/" + this.props.componentCategory.categoryGuid + "/month/" + date,
                data: filters,
                type: "POST",
                success: (data: MarketingData.PeriodData) => {
                    if (this.props.filters === filters
                        && this.props.componentCategory.categoryGuid === categoryGuid
                        && this.state.dateMonth === date) {
                        this.log("Success retrieving month to date data");
                        this.log(data);
                        this.setState({
                            monthStatus: MarketingTabStatus.Ready,
                            dataMonth: data,
                        });
                    } else {
                        this.log("Retrieved month data but filters/date changed");
                    }
                },
            },
        );
    }

    private retrieveYearData(date: string): void {
        this.log("Retrieving year data: " + date);
        // Copy the filters to check them when the data is retrieved.
        const filters = this.props.filters;
        const categoryGuid = this.props.componentCategory.categoryGuid;
        Ajax.ajaxRequestForJson(
            {
                url: "locations/marketing/" + this.props.componentCategory.categoryGuid + "/year/" + date,
                data: filters,
                type: "POST",
                success: (data: MarketingData.PeriodData) => {
                    if (this.props.filters === filters
                        && this.props.componentCategory.categoryGuid === categoryGuid
                        && this.state.dateYear === date) {
                        this.log("Success retrieving year data");
                        this.log(data);
                        this.setState({
                            yearStatus: MarketingTabStatus.Ready,
                            dataYear: data,
                        });
                    } else {
                        this.log("Retrieved year data but filters/date changed");
                    }
                },
            },
        );
    }

    private retrieveRolling4WeeksData(date: string): void {
        this.log("Retrieving rolling 4 weeks data: " + date);
        // Copy the filters to check them when the data is retrieved.
        const filters = this.props.filters;
        const categoryGuid = this.props.componentCategory.categoryGuid;
        Ajax.ajaxRequestForJson(
            {
                url: "locations/marketing/" + this.props.componentCategory.categoryGuid + "/rolling4Weeks/" + date,
                data: filters,
                type: "POST",
                success: (data: MarketingData.RollingPeriodWithWeeklySales) => {
                    if (this.props.filters === filters
                        && this.props.componentCategory.categoryGuid === categoryGuid
                        && this.state.dateRolling4Weeks === date) {
                        this.log("Success retrieving rolling 4 weeks data");
                        this.log(data);
                        this.setState({
                            rolling4WeeksStatus: MarketingTabStatus.Ready,
                            dataRolling4Weeks: data,
                        });
                    } else {
                        this.log("Retrieved rolling 4 weeks data but filters/date changed");
                    }
                },
            },
        );
    }

    private retrieveRollingQuarterData(date: string): void {
        this.log("Retrieving rolling quarter data: " + date);
        // Copy the filters to check them when the data is retrieved.
        const filters = this.props.filters;
        const categoryGuid = this.props.componentCategory.categoryGuid;
        Ajax.ajaxRequestForJson(
            {
                url: "locations/marketing/" + this.props.componentCategory.categoryGuid + "/rollingQuarter/" + date,
                data: filters,
                type: "POST",
                success: (data: MarketingData.RollingPeriodWithMonthlySales) => {
                    if (this.props.filters === filters
                        && this.props.componentCategory.categoryGuid === categoryGuid
                        && this.state.dateRollingQuarter === date) {
                        this.log("Success retrieving rolling quarter data");
                        this.log(data);
                        this.setState({
                            rollingQuarterStatus: MarketingTabStatus.Ready,
                            dataRollingQuarter: data,
                        });
                    } else {
                        this.log("Retrieved rolling quarter data but filters/date changed");
                    }
                },
            },
        );
    }

    private retrieveRollingSemesterData(date: string): void {
        this.log("Retrieving rolling semester data: " + date);
        // Copy the filters to check them when the data is retrieved.
        const filters = this.props.filters;
        const categoryGuid = this.props.componentCategory.categoryGuid;
        Ajax.ajaxRequestForJson(
            {
                url: "locations/marketing/" + this.props.componentCategory.categoryGuid + "/rollingSemester/" + date,
                data: filters,
                type: "POST",
                success: (data: MarketingData.RollingPeriodWithMonthlySales) => {
                    if (this.props.filters === filters
                        && this.props.componentCategory.categoryGuid === categoryGuid
                        && this.state.dateRollingSemester === date) {
                        this.log("Success retrieving rolling semester data");
                        this.log(data);
                        this.setState({
                            rollingSemesterStatus: MarketingTabStatus.Ready,
                            dataRollingSemester: data,
                        });
                    } else {
                        this.log("Retrieved rolling semester data but filters/date changed");
                    }
                },
            },
        );
    }

    private retrieveRollingYearData(date: string): void {
        this.log("Retrieving rolling year data: " + date);
        // Copy the filters to check them when the data is retrieved.
        const filters = this.props.filters;
        const categoryGuid = this.props.componentCategory.categoryGuid;
        Ajax.ajaxRequestForJson(
            {
                url: "locations/marketing/" + this.props.componentCategory.categoryGuid + "/rollingYear/" + date,
                data: filters,
                type: "POST",
                success: (data: MarketingData.RollingPeriodWithMonthlySales) => {
                    if (this.props.filters === filters
                        && this.props.componentCategory.categoryGuid === categoryGuid
                        && this.state.dateRollingYear === date) {
                        this.log("Success retrieving rolling year data");
                        this.log(data);
                        this.setState({
                            rollingYearStatus: MarketingTabStatus.Ready,
                            dataRollingYear: data,
                        });
                    } else {
                        this.log("Retrieved rolling year data but filters/date changed");
                    }
                },
            },
        );
    }

    private changePeriod(period: PeriodSelection): void {
        this.log("Changing period to: " + period);
        this.setState({period});
    }

    private changeTime(date: string): void {
        this.log("Changing date to: " + date);
        switch (this.state.period) {
            case PeriodSelection.Week:
                this.setState({dateWeek: date, weekStatus: MarketingTabStatus.Initialized});
                break;
            case PeriodSelection.Month:
                this.setState({dateMonth: date, monthStatus: MarketingTabStatus.Initialized});
                break;
            case PeriodSelection.Year:
                this.setState({dateYear: date, yearStatus: MarketingTabStatus.Initialized});
                break;
            case PeriodSelection.Rolling4Weeks:
                this.setState({dateRolling4Weeks: date, rolling4WeeksStatus: MarketingTabStatus.Initialized});
                break;
            case PeriodSelection.RollingQuarter:
                this.setState({dateRollingQuarter: date, rollingQuarterStatus: MarketingTabStatus.Initialized});
                break;
            case PeriodSelection.RollingSemester:
                this.setState({dateRollingSemester: date, rollingSemesterStatus: MarketingTabStatus.Initialized});
                break;
            case PeriodSelection.RollingYear:
                this.setState({dateRollingYear: date, rollingYearStatus: MarketingTabStatus.Initialized});
                break;
            default:
                console.error("PeriodSelection " + this.state.period + " not recognized");
        }
    }

    private buildTitle(config: MarketingConfig.Container, period: PeriodSelection) {
        let title: string = config.title.title;
        let beginDate: Moment;
        let endDate: Moment;
        if (period === PeriodSelection.Week) {
            beginDate = moment(this.state.dateWeek);
            endDate = moment(this.state.dateWeek).endOf("isoWeek");
        } else if (period === PeriodSelection.Month) {
            beginDate = moment(this.state.dateMonth);
            endDate = moment(this.state.dateMonth).endOf("month");
        } else if (period === PeriodSelection.Year) {
            beginDate = moment(this.state.dateYear);
            endDate = moment(this.state.dateYear).endOf("year");
        } else if (period === PeriodSelection.Rolling4Weeks) {
            beginDate = moment(this.state.dateRolling4Weeks).add(-3, "weeks");
            endDate = moment(this.state.dateRolling4Weeks).endOf("isoWeek");
        } else if (period === PeriodSelection.RollingQuarter) {
            beginDate = moment(this.state.dateRollingQuarter).add(-2, "months");
            endDate = moment(this.state.dateRollingQuarter).endOf("month");
        } else if (period === PeriodSelection.RollingSemester) {
            beginDate = moment(this.state.dateRollingSemester).add(-5, "months");
            endDate = moment(this.state.dateRollingSemester).endOf("month");
        } else if (period === PeriodSelection.RollingYear) {
            beginDate = moment(this.state.dateRollingYear).add(-11, "months");
            endDate = moment(this.state.dateRollingYear).endOf("month");
        }
        const reactStringReplace = require("react-string-replace");
        const dateElement = (str: string, pattern: string, value: string): string =>
            reactStringReplace(str, pattern, () => (<span className="header__date">{value}</span>));
        title = dateElement(title, "BEGIN_DATE", MarketingTab.formatUserDate(beginDate));
        title = dateElement(title, "END_DATE", MarketingTab.formatUserDate(endDate));
        return title;
    }

    private exportToExcel(date: string): void {
        const period = this.state.period;
        Ajax.ajaxRequestForJson(
            {
                url: "locations/marketing/export/params",
                data: this.props.filters,
                type: "POST",
                success: () => {
                    let periodURI: string;
                    if (period === PeriodSelection.Month) {
                        periodURI = "month";
                    } else if (period === PeriodSelection.Week) {
                        periodURI = "week";
                    } else if (period === PeriodSelection.Year) {
                        periodURI = "year";
                    } else if (period === PeriodSelection.RollingYear) {
                        periodURI = "rollingYear";
                    } else if (period === PeriodSelection.RollingQuarter) {
                        periodURI = "rollingQuarter";
                    } else if (period === PeriodSelection.RollingSemester) {
                        periodURI = "rollingSemester";
                    } else if (period === PeriodSelection.Rolling4Weeks) {
                        periodURI = "rolling4Weeks";
                    }
                    if (this.props.isDetailLevel) {
                        window.open(
                            "location/marketing/"
                            + this.props.componentCategory.categoryGuid
                            + "/" + periodURI + "/export/" + date
                            + "/" + this.state.unitOfMeasure.code,
                            "_self",
                        );
                    } else {
                        window.open("locations/marketing/"
                            + this.props.componentCategory.categoryGuid
                            + "/" + periodURI + "/export/" + date
                            + "/" + this.state.unitOfMeasure.code,
                            "_self",
                        );
                    }
                },
            },
        );
    }

    // noinspection JSMethodCanBeStatic
    private log(message: any): void {
        if (MarketingTab.logEnabled) {
            console.log(message);
        }
    }
}
